import React from "react";

interface SlideContent {
  title?: string;
  subtitle?: string;
  exitType?: string;
  exitNote?: string;
  emoji?: string;
  wrapUpText?: string;
  leftEmoji?: string;
  rightEmoji?: string;
  bgColor?: string;
  sparkleEmoji?: string;
  waveEmoji?: string;
  decorativeDots?: boolean;
  decorativeLines?: boolean;
  onEdit?: (content: SlideContent) => void;
}

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const ExitSlide = {
  id: "exit-slide",
  name: "Exit Slide",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "",
    },
    {
      id: "exitType",
      label: "Exit Type",
      type: "text",
      defaultValue: "",
    },
    {
      id: "exitNote",
      label: "Exit Note",
      type: "text",
      defaultValue: "",
    },
    {
      id: "emoji",
      label: "Theme Emoji",
      type: "text",
      defaultValue: "",
    },
    {
      id: "wrapUpText",
      label: "Wrap Up Text",
      type: "text",
      defaultValue: "",
    },
    {
      id: "leftEmoji",
      label: "Left Corner Emoji",
      type: "text",
      defaultValue: "",
    },
    {
      id: "rightEmoji",
      label: "Right Corner Emoji",
      type: "text",
      defaultValue: "",
    },
    {
      id: "sparkleEmoji",
      label: "Sparkle Emoji",
      type: "text",
      defaultValue: "",
    },
    {
      id: "waveEmoji",
      label: "Wave Emoji",
      type: "text",
      defaultValue: "",
    },
    {
      id: "decorativeDots",
      label: "Show Decorative Dots",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "decorativeLines",
      label: "Show Decorative Lines",
      type: "boolean",
      defaultValue: true,
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "",
      subtitle = "",
      exitType = "",
      exitNote = "",
      emoji = "",
      wrapUpText = "",
      leftEmoji = "",
      rightEmoji = "",
      sparkleEmoji = "",
      waveEmoji = "",
      decorativeDots = true,
      decorativeLines = true,
      bgColor = "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0">
              {decorativeDots &&
                [...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-[800px] h-[800px] border border-white/10 rounded-full"
                    style={{
                      top: "50%",
                      left: "50%",
                      transform: `translate(-50%, -50%) scale(${0.5 + i * 0.2})`,
                    }}
                  />
                ))}
            </div>
          </div>
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full p-12">
          {/* Main Content Box */}
          <div className="max-w-4xl w-full">
            {/* Top Decorative Line */}
            {decorativeLines && (
              <div className="flex justify-center gap-4 mb-8">
                <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-sky-400/30 to-transparent mt-4" />
                <EditableDiv
                  text={emoji}
                  onEdit={handleEdit("emoji")}
                  className="text-5xl transform -rotate-12"
                />
                <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-sky-400/30 to-transparent mt-4" />
              </div>
            )}

            {/* Title Section */}
            <div className="text-center mb-8">
              <div className="inline-block">
                <h2 className="text-5xl font-black mb-3 relative">
                  <EditableDiv
                    text={wrapUpText}
                    onEdit={handleEdit("wrapUpText")}
                    className="relative text-sky-200"
                  />
                </h2>
                {decorativeLines && (
                  <div className="h-1 w-3/4 mx-auto bg-gradient-to-r from-transparent via-sky-400/30 to-transparent" />
                )}
              </div>
              <EditableDiv
                text={subtitle}
                onEdit={handleEdit("subtitle")}
                className="text-xl text-sky-200/80 mt-4"
              />
            </div>

            {/* Exit Type Badge */}
            <div className="flex justify-center mb-8">
              <div
                className="px-6 py-2 rounded-full bg-sky-500/20 text-sky-200 font-mono border border-sky-500/30
                            transform hover:scale-105 transition-transform"
              >
                <div className="flex items-center gap-3">
                  <EditableDiv
                    text={sparkleEmoji}
                    onEdit={handleEdit("sparkleEmoji")}
                    className="text-xl"
                  />
                  <EditableDiv
                    text={exitType}
                    onEdit={handleEdit("exitType")}
                    className="text-lg"
                  />
                  <EditableDiv
                    text={sparkleEmoji}
                    onEdit={handleEdit("sparkleEmoji")}
                    className="text-xl"
                  />
                </div>
              </div>
            </div>

            {/* Exit Note */}
            <div
              className="text-center bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10
                          transform hover:scale-102 transition-transform"
            >
              <EditableDiv
                text={exitNote}
                onEdit={handleEdit("exitNote")}
                className="text-xl text-sky-100/90 leading-relaxed"
              />
            </div>

            {/* Bottom Decorative Elements */}
            {decorativeDots && (
              <div className="mt-12 flex justify-center items-center gap-6">
                <div className="w-3 h-3 rounded-full bg-sky-400/60" />
                <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-sky-400/30 to-transparent" />
                <EditableDiv
                  text={waveEmoji}
                  onEdit={handleEdit("waveEmoji")}
                  className="text-3xl"
                />
                <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-sky-400/30 to-transparent" />
                <div className="w-3 h-3 rounded-full bg-sky-400/60" />
              </div>
            )}
          </div>
        </div>

        {/* Corner Decorations */}
        <div className="absolute top-8 right-8 text-4xl transform rotate-12">
          <EditableDiv
            text={leftEmoji}
            onEdit={handleEdit("leftEmoji")}
            className=""
          />
        </div>
        <div className="absolute bottom-8 left-8 text-4xl transform -rotate-12">
          <EditableDiv
            text={rightEmoji}
            onEdit={handleEdit("rightEmoji")}
            className=""
          />
        </div>
      </div>
    );
  },
};

declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default ExitSlide;
